import { useState } from 'react';
import { TableDataType } from '../_types';
import { LINKS } from '@/constants';
/**
 * 유전자 커버리지 excel파일 다운로드 기능을 관리하는 훅
 *
 * 주요 기능:
 * 1. 다운로드 버튼 클릭 시 로딩 상태 관리 (isDownloadLoading)
 * 2. 다운로드 기능 구현 (handleDownload)
 * 3. 다운로드 완료 후 로딩 상태 해제
 *
 * @returns {Object} 다운로드 관련 상태와 함수
 * @returns {boolean} isDownloadLoading - 현재 다운로드 진행 중인지 여부
 * @returns {Function} handleDownload - 다운로드 실행 함수
 */

export function useDownload(searchFilteredGeneData: TableDataType[]) {
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const handleDownload = async () => {
    setIsDownloadLoading(true);

    try {
      // 필요할 때만 xlsx 라이브러리 동적 임포트
      const { utils, writeFile } = await import('xlsx-js-style');

      // 헤더 이름 매핑 - 함수 외부로 이동하여 재사용 가능하게 함
      const headerMapping: Record<string, string> = {
        geneName: 'geneName (HGNC)',
        coverage20x: 'coverage20x (3B-EXOME)',
        capturedByWes: 'capturedBy3B-EXOME',
        wesDeadzone: '3B-EXOME Deadzone',
        wesExonsDeadzone: '3B-EXOME Exons Deadzone',
        coverage10x: 'coverage10x (3B-GENOME)',
        wgsDeadzone: '3B-GENOME Deadzone',
        wgsExonsDeadzone: '3B-GENOME Exons Deadzone',
      };

      // Gene Browser 링크 열 추가 및 인덱스 열 추가
      const dataWithLinks = searchFilteredGeneData.map((row, index) => {
        const geneName = row.geneName || '';
        return {
          index: index + 1, // 1부터 시작하는 인덱스 추가
          ...row,
          'Link to 3billion Gene Browser': geneName
            ? {
                v: geneName,
                l: {
                  Target: `${LINKS.GENE_BROWSER_GENE}/${geneName}`,
                  Tooltip: `Open ${geneName} in Gene Browser`,
                },
                s: {
                  font: {
                    italic: true,
                    underline: true,
                  },
                },
              }
            : '',
        };
      });

      // 워크북 생성
      const workbook = utils.book_new();

      // 워크시트 생성
      const worksheet = utils.json_to_sheet(dataWithLinks);

      // 헤더 텍스트 길이에 맞게 열 너비 자동 조정
      const headerWidths: Record<number, number> = {};

      // 첫 번째 행(헤더)의 각 셀 가져오기
      const headers = Object.keys(dataWithLinks[0] || {});

      // 헤더 이름 변경 및 너비 설정 로직 개선
      if (worksheet['A1'] && headers.length > 0) {
        // 한 번의 순회로 헤더 이름 변경과 너비 설정을 동시에 처리
        headers.forEach((header, index) => {
          const columnLetter = String.fromCharCode(65 + index); // A, B, C, ...
          const cell = worksheet[`${columnLetter}1`];

          if (cell && headerMapping[header]) {
            // 헤더 이름 변경
            const newHeaderName = headerMapping[header];
            cell.v = newHeaderName;
            cell.w = newHeaderName;

            // 변경된 헤더에 대한 너비 설정
            headerWidths[index] = newHeaderName.length + 2;
          } else {
            // 기본 헤더 너비 설정
            headerWidths[index] = Math.max(header.length + 2, 8);
          }
        });
      }

      // 열 너비 설정 (중복 코드 제거)
      worksheet['!cols'] = headers.map((_, index) => ({
        width: headerWidths[index] || 8,
      }));

      // 워크시트를 워크북에 추가
      utils.book_append_sheet(workbook, worksheet);

      // 현재 날짜를 파일명에 포함
      const date = new Date();
      const dateString = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
      const fileName = `gene_coverage_data_${dateString}.xlsx`;

      // 파일 다운로드
      writeFile(workbook, fileName);
    } catch (error) {
      console.error('Excel 파일 다운로드 중 오류 발생:', error);
    } finally {
      setIsDownloadLoading(false);
    }
  };

  return {
    isDownloadLoading,
    handleDownload,
  };
}
